import React from 'react';
import { X, Lock, Trophy } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../lib/utils';
import { ACHIEVEMENTS } from '../lib/constants';
import { useSettings } from './SettingsContext';

// Rarity tier colors
const RARITY_COLORS = {
  easy: {
    border: 'border-emerald-500/30',
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-400',
    badge: 'bg-emerald-500/20 text-emerald-400',
  },
  medium: {
    border: 'border-blue-500/30',
    bg: 'bg-blue-500/10',
    text: 'text-blue-400',
    badge: 'bg-blue-500/20 text-blue-400',
  },
  hard: {
    border: 'border-purple-500/30',
    bg: 'bg-purple-500/10',
    text: 'text-purple-400',
    badge: 'bg-purple-500/20 text-purple-400',
  },
  legendary: {
    border: 'border-yellow-500/40',
    bg: 'bg-yellow-500/10',
    text: 'text-yellow-400',
    badge: 'bg-yellow-500/20 text-yellow-400',
  },
};

function getRarityFromCoinReward(coinReward: number): keyof typeof RARITY_COLORS {
  if (coinReward >= 80) return 'legendary';
  if (coinReward >= 40) return 'hard';
  if (coinReward >= 20) return 'medium';
  return 'easy';
}

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

interface AchievementCardProps {
  achievement: (typeof ACHIEVEMENTS)[number];
  unlocked: boolean;
  unlockedDate?: number;
  index: number;
}

function AchievementCard({ achievement, unlocked, unlockedDate, index }: AchievementCardProps) {
  const rarity = getRarityFromCoinReward(achievement.coinReward);
  const colors = RARITY_COLORS[rarity];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04, duration: 0.3 }}
      className={cn(
        'relative rounded-2xl p-3.5 border transition-all duration-300 flex flex-col gap-2',
        unlocked
          ? `${colors.border} ${colors.bg} shadow-lg`
          : 'border-white/5 bg-white/5 opacity-50 grayscale'
      )}
    >
      {/* Rarity badge */}
      <div className="flex items-center justify-between">
        <span className={cn('text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full', colors.badge)}>
          {rarity}
        </span>
        <span className={cn('text-sm font-bold', colors.text)}>
          {unlocked ? '🏆' : <Lock className="w-3.5 h-3.5 text-white/30" />}
        </span>
      </div>

      {/* Icon */}
      <div className={cn(
        'w-10 h-10 rounded-xl flex items-center justify-center text-xl',
        unlocked ? colors.bg : 'bg-white/5'
      )}>
        {unlocked ? '🏆' : '🔒'}
      </div>

      {/* Name & Description */}
      <div className="space-y-0.5">
        <h3 className={cn(
          'text-sm font-bold leading-tight',
          unlocked ? 'text-white' : 'text-white/60'
        )}>
          {achievement.name}
        </h3>
        <p className={cn(
          'text-[11px] leading-snug',
          unlocked ? 'text-white/60' : 'text-white/40'
        )}>
          {achievement.description}
        </p>
      </div>

      {/* Footer: coin reward + unlock date */}
      <div className="flex items-center justify-between mt-auto pt-1">
        <span className={cn(
          'text-xs font-bold flex items-center gap-1',
          unlocked ? 'text-yellow-400' : 'text-white/30'
        )}>
          +{achievement.coinReward} 🪙
        </span>
        {unlocked && unlockedDate && (
          <span className="text-[10px] text-white/40">
            {formatDate(unlockedDate)}
          </span>
        )}
      </div>
    </motion.div>
  );
}

interface AchievementsModalProps {
  achievements: Record<string, boolean | number>; // { [id]: true | timestamp }
  onClose: () => void;
}

export default function AchievementsModal({ achievements, onClose }: AchievementsModalProps) {
  const { playSfx, triggerHaptic } = useSettings();
  const unlockedIds = Object.keys(achievements).filter(k => achievements[k]);
  const unlockedCount = unlockedIds.length;
  const totalCount = ACHIEVEMENTS.length;

  const handleClose = () => {
    playSfx('click');
    triggerHaptic('light');
    onClose();
  };

  return (
    <AnimatePresence>
      {(
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-xl z-[100] overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        >
          {/* Ambient glow */}
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[300px] bg-yellow-500/5 rounded-full blur-[120px] pointer-events-none" />

          <div className="min-h-full flex flex-col items-center p-4 pb-8">
            {/* Header */}
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.05 }}
              className="w-full max-w-2xl flex items-center justify-between pt-6 pb-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <h2 className="text-xl font-black text-white uppercase tracking-wide">
                    Obiettivi
                  </h2>
                  <p className="text-xs text-white/50">
                    {unlockedCount} / {totalCount} sbloccati
                  </p>
                </div>
              </div>

              <button
                onClick={handleClose}
                className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all active:scale-95 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </motion.div>

            {/* Progress bar */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="w-full max-w-2xl mb-4"
            >
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-yellow-500 to-amber-400 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${(unlockedCount / totalCount) * 100}%` }}
                  transition={{ delay: 0.2, duration: 0.6, ease: 'easeOut' }}
                />
              </div>
            </motion.div>

            {/* Grid: 2 cols mobile, 3 cols sm, 4 cols lg */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15 }}
              className="w-full max-w-2xl grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
            >
              {ACHIEVEMENTS.map((achievement, index) => (
                <div key={achievement.id}>
                  <AchievementCard
                    achievement={achievement}
                    unlocked={unlockedIds.includes(achievement.id)}
                    unlockedDate={typeof achievements[achievement.id] === 'number' ? achievements[achievement.id] as number : undefined}
                    index={index}
                  />
                </div>
              ))}
            </motion.div>

            {/* Bottom padding for scroll */}
            <div className="h-8" />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}